import * as React from 'react';
import { groupedData } from '../FieldRenderingEngine/util';
import { DATE_FORMAT, TIME_FORMAT } from '../../containers/WorkkardDetail/constants';
import { fetchTimezoneAbbreviation } from '../../common/util';
const moment = require('moment');

const RICH_TEXT_TYPES = ['richtext', 'richtexteditor'];
const RICH_TEXT_ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 's', 'strike', 'u', 'mark', 'sup', 'sub'];

export function sanitizeRichTextForListing(html) {
    if (!html) return '';
    let str = String(html);

    str = str.replace(/<\/?(p|div|br)[^>]*>/gi, ' ');
    str = str.replace(/&nbsp;/gi, ' ');

    str = str.replace(/<(\/?)([a-zA-Z0-9]+)[^>]*>/g, (match, closing, tag) => {
        tag = tag.toLowerCase();
        return RICH_TEXT_ALLOWED_TAGS.includes(tag) ? `<${closing}${tag}>` : '';
    });

    let prev;
    do {
        prev = str;
        str = str.replace(/<(\w+)>\s*<\/\1>/g, '');
    } while (str !== prev);

    return str.replace(/\s+/g, ' ').trim();
}

function isRichTextField(field) {
    if (RICH_TEXT_TYPES.includes((field.FieldType || '').toLowerCase())) return true;
    const value = field.Value;
    return typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);
}

function renderValue(field, value) {
    if (!isRichTextField(field)) return value;
    return <span className="richTextValue" dangerouslySetInnerHTML={{ __html: value }} />;
}

function getDisplayValue(field, { returnValueForBlank = '-' } = {}) {
    const { Value, FieldType } = field;
    if (Value === null || Value === undefined) return returnValueForBlank;
    const str = String(Value).trim();
    if (!str) return returnValueForBlank;

    if (FieldType === 'date') {
        return moment.utc(Value).isValid() ? moment.utc(Value).format(DATE_FORMAT) : returnValueForBlank;
    }
    if (FieldType === 'datetime' || FieldType === 'datetimetimezone') {
        return moment.utc(Value).isValid() ? moment.utc(Value).format(TIME_FORMAT) : returnValueForBlank;
    }
    if (FieldType === 'multiplechoicelist') {
        return str.split(',').join(', ');
    }
    if (isRichTextField(field)) {
        const cleaned = sanitizeRichTextForListing(str);
        return cleaned || returnValueForBlank;
    }
    return str;
}

function renderGroup(group, opts, idx) {
    if (!group || !group.length) return null;

    const {
        renderLocation,
        renderType,
        showLabel,
        returnValueForBlank,
        userTimezone,
    } = opts;

    const firstField = group[0];
    const fieldGroup  = firstField.FieldGroup;
    const groupType   = (firstField.Group || '').toLowerCase();
    const label       = firstField.Label || fieldGroup;
    const rt          = (renderType || '').toLowerCase();

    if (firstField.FieldKey && firstField.FieldKey.includes('_timezone_')) return null;

    if (groupType === 'address') {
        const f = (ident) => group.find(x => x.Name && x.Name.toLowerCase().includes(ident));
        const parts = [f('address1')?.Value, f('address2')?.Value].filter(Boolean);
        const cszParts = [f('city')?.Value, f('state')?.Value, f('zip')?.Value].filter(Boolean);
        if (cszParts.length) parts.push(cszParts.join(' '));
        const addrStr = parts.join(', ') || returnValueForBlank;
        return (
            <span className="rowFlex" key={idx} data-test={fieldGroup}>
                {showLabel && <label>{fieldGroup}</label>}
                <span className="address">{addrStr}</span>
            </span>
        );
    }

    if (groupType === 'name') {
        const f = (ident) => group.find(x => x.Name && x.Name.toLowerCase().includes(ident));
        const first = f('firstname')?.Value || '';
        const last  = f('lastname')?.Value  || '';
        const nameVal = (first && last)
            ? `${last}, ${first}`
            : (first || last || returnValueForBlank);

        if (renderLocation === 'card') {
            return showLabel
                ? <li key={idx} data-test={label}><label>{label || fieldGroup} </label>: <span>{nameVal}</span></li>
                : <span key={idx} className="wkName">{nameVal}</span>;
        }
        if (renderLocation === 'grid') {
            return showLabel
                ? <li key={idx} data-test={label}><label>{label}{rt === 'activity' ? ': ' : ' '}</label><span>{nameVal}</span></li>
                : (rt === 'activity' ? <li key={idx} data-test={nameVal}>{nameVal}</li> : nameVal);
        }
        return showLabel
            ? <li key={idx} data-test={label}><label>{label}</label><span>{nameVal}</span></li>
            : nameVal;
    }

    if (group.length > 1) {
        const dtField = group.find(x => x.FieldKey && x.FieldKey.includes('_datetime_'));
        const tzField = group.find(x => x.FieldKey && x.FieldKey.includes('_timezone_'));
        if (dtField) {
            const dtVal = dtField.Value && moment.utc(dtField.Value).isValid()
                ? moment.utc(dtField.Value).format(TIME_FORMAT) : '';
            const tzVal = tzField?.Value ? fetchTimezoneAbbreviation(tzField.Value) : '';
            const combined = [dtVal, tzVal].filter(Boolean).join(' ') || returnValueForBlank;

            if (renderLocation === 'card') {
                return showLabel
                    ? <li key={idx} data-test={label}><label>{label || fieldGroup} </label>: <span>{combined}</span></li>
                    : combined;
            }
            return showLabel
                ? <li key={idx} data-test={label}><label>{label}</label><span>{combined}</span></li>
                : combined;
        }
    }

    const field = group[0];
    const value = getDisplayValue(field, { returnValueForBlank, userTimezone });
    const content = renderValue(field, value);

    if (renderLocation === 'card') {
        return showLabel
            ? <li key={idx} data-test={label}><label>{label || fieldGroup} </label>: <span>{content}</span></li>
            : content;
    }

    if (renderLocation === 'grid') {
        if (rt === 'activity') {
            return showLabel
                ? <li key={idx} data-test={label}><label>{label}: </label>{content}</li>
                : <li key={idx} data-test={value}>{content}</li>;
        }
        return showLabel
            ? <li key={idx} data-test={label}><label>{label} </label><span>{content}</span></li>
            : content;
    }

    if (rt === 'activity' || rt === 'activitylisting') {
        return showLabel
            ? <li key={idx} data-test={label}><label>{label}</label><span>{content}</span></li>
            : <li key={idx} data-test={value}>{content}</li>;
    }
    if (rt === 'workkard') {
        return showLabel
            ? <li key={idx} data-test={label}><label>{label} </label><span>{content}</span></li>
            : content;
    }
    return showLabel
        ? <React.Fragment key={idx}><label>{label}: </label>{content}</React.Fragment>
        : content;
}

const FieldDisplayEngine = (props) => {
    const {
        data            = [],
        renderLocation  = 'detail',
        renderType      = 'workkard',
        includeFields   = [],
        excludeFields   = [],
        showLabel       = false,
        returnValueForBlank = '-',
        userTimezone,
        tzData,
    } = props;

    if (!data || !data.length) return null;

    const grouped  = groupedData(data);
    const filtered = grouped.filter(group => {
        if (!group || !group.length) return false;
        const fg = group[0].FieldGroup || group[0].field_group_name;
        if (includeFields.length > 0) return includeFields.includes(fg);
        return !excludeFields.includes(fg);
    });

    if (!filtered.length) return null;

    const opts = { renderLocation, renderType, showLabel, returnValueForBlank, userTimezone, tzData };

    return (
        <React.Fragment>
            {filtered.map((group, i) => renderGroup(group, opts, i))}
        </React.Fragment>
    );
};

export default FieldDisplayEngine;
